import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";

export interface PendingEdit {
  type: "deal" | "coupon" | "offer";
  id: string;
  entityName: string;
  original: Record<string, any>;
  changes: Record<string, any>;
}

interface EditSessionContextType {
  pendingEdits: Map<string, PendingEdit>;
  editCount: number;
  addEdit: (edit: PendingEdit) => void;
  removeEdit: (type: string, id: string) => void;
  clearAll: () => void;
  getEditsArray: () => PendingEdit[];
  isStaging: boolean;
}

const EditSessionContext = createContext<EditSessionContextType | undefined>(
  undefined
);

// Used when an editor renders outside a provider: callers save directly instead of staging.
const standaloneEditSession: EditSessionContextType = {
  pendingEdits: new Map(),
  editCount: 0,
  addEdit: () => {},
  removeEdit: () => {},
  clearAll: () => {},
  getEditsArray: () => [],
  isStaging: false,
};

function editKey(type: string, id: string) {
  return `${type}:${id}`;
}

export const EditSessionProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(
    () => new Map()
  );

  const addEdit = useCallback((edit: PendingEdit) => {
    setPendingEdits((prev) => {
      const next = new Map(prev);
      next.set(editKey(edit.type, edit.id), edit);
      return next;
    });
  }, []);

  const removeEdit = useCallback((type: string, id: string) => {
    setPendingEdits((prev) => {
      const next = new Map(prev);
      next.delete(editKey(type, id));
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setPendingEdits(new Map());
  }, []);

  const getEditsArray = useCallback(
    () => Array.from(pendingEdits.values()),
    [pendingEdits]
  );

  const editCount = pendingEdits.size;

  const value = useMemo(
    () => ({
      pendingEdits,
      editCount,
      addEdit,
      removeEdit,
      clearAll,
      getEditsArray,
      isStaging: true,
    }),
    [pendingEdits, editCount, addEdit, removeEdit, clearAll, getEditsArray]
  );

  return (
    <EditSessionContext.Provider value={value}>
      {children}
    </EditSessionContext.Provider>
  );
};

export const useEditSession = () => {
  return useContext(EditSessionContext) ?? standaloneEditSession;
};
